import React, { createContext, useContext, useState, useEffect } from 'react';
import { config } from '../utils/config';
// import { Auth } from 'aws-amplify'; // v6 import changes, commented for now

// User roles enum
export const UserRole = {
  ADMIN: 'ADMIN',
  USER: 'USER'
};

// Permission constants
export const permissions = {
  ADMIN: [
    'domains:create',
    'domains:delete', 
    'domains:manage',
    'links:manage-all',
    'analytics:view-all',
    'users:view'
  ],
  USER: [
    'links:create',
    'links:manage-own',
    'analytics:view-own'
  ]
};

// Create Auth Context
const AuthContext = createContext();

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Auth Provider Component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if user is authenticated on mount
  useEffect(() => {
    checkUser();
  }, []);

  const checkUser = async () => {
    try {
      setIsLoading(true);
      console.log('🔍 Checking user authentication...');
      console.log('🔧 Demo mode:', config.isDemoMode);
      console.log('🌐 Environment:', process.env.REACT_APP_ENVIRONMENT);
      
      if (config.isDemoMode) {
        // Demo mode - check localStorage
        console.log('🎭 DEMO MODE: Checking localStorage for demo user');
        const savedUser = localStorage.getItem('demo-user');
        if (savedUser) {
          const userData = JSON.parse(savedUser);
          console.log('👤 Found demo user:', userData.email);
          setUser(userData);
        } else {
          console.log('❌ No demo user found in localStorage');
          setUser(null);
        }
      } else {
        // Production mode - check for JWT token
        console.log('🚀 PRODUCTION MODE: Checking JWT token authentication');
        const savedToken = localStorage.getItem('jwt-token');
        const savedUser = localStorage.getItem('production-user');
        
        if (savedToken && savedUser) {
          try {
            // Simple token validation (in real production, verify JWT signature)
            const user = JSON.parse(savedUser);
            console.log('👤 Found production user:', user.email);
            setUser(user);
          } catch (error) {
            console.log('❌ Invalid saved user data, clearing...');
            localStorage.removeItem('jwt-token');
            localStorage.removeItem('production-user');
            setUser(null);
          }
        } else {
          console.log('❌ No production user found - showing login form');
          setUser(null); // Force user to login screen
        }
      }
      setError(null);
    } catch (err) {
      console.log('❌ Error checking user:', err);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const formatUserData = (userData) => {
    const role = userData.role || UserRole.USER;
    
    return {
      id: userData.id || userData.email,
      email: userData.email,
      firstName: userData.firstName || '',
      lastName: userData.lastName || '',
      role: role,
      permissions: permissions[role] || permissions.USER,
      createdAt: userData.createdAt || new Date().toISOString(),
      emailVerified: userData.emailVerified !== false
    };
  };

  const signIn = async (email, password) => {
    try {
      setIsLoading(true);
      setError(null);
      
      if (!email || !password) {
        throw new Error('Email and password are required');
      }

      if (config.isDemoMode) {
        // Demo mode authentication
        const userData = formatUserData({
          email: email,
          firstName: email.split('@')[0],
          lastName: 'User',
          role: email.includes('admin') ? UserRole.ADMIN : UserRole.USER,
          emailVerified: true
        });
        
        localStorage.setItem('demo-user', JSON.stringify(userData));
        setUser(userData);
        return userData;
      } else {
        // Production mode - simple JWT authentication
        console.log('🔐 PRODUCTION MODE: Attempting login...');
        
        if (email === process.env.REACT_APP_ADMIN_EMAIL && password === process.env.REACT_APP_ADMIN_PASSWORD) {
          const userData = {
            id: 'admin-user',
            email: process.env.REACT_APP_ADMIN_EMAIL,
            firstName: 'Admin',
            lastName: 'User',
            role: UserRole.ADMIN,
            permissions: permissions[UserRole.ADMIN],
            emailVerified: true
          };
          
          // Generate simple JWT token (placeholder)
          const token = btoa(JSON.stringify({ 
            user: userData, 
            exp: Date.now() + (24 * 60 * 60 * 1000) // 24 hours
          }));
          
          localStorage.setItem('jwt-token', token);
          localStorage.setItem('production-user', JSON.stringify(userData));
          setUser(userData);
          
          console.log('✅ Production login successful');
          return userData;
        } else {
          throw new Error('Invalid credentials. Please check your email and password.');
        }
      }
    } catch (err) {
      const errorMessage = getAuthErrorMessage(err);
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const signUp = async (userData) => {
    try {
      setIsLoading(true);
      setError(null);

      const { email, password, firstName, lastName, role = UserRole.USER } = userData;

      // Simulate sign up - in production this would use AWS Amplify
      if (email && password && firstName && lastName) {
        return {
          user: { email },
          userSub: 'demo-' + Date.now(),
          codeDeliveryDetails: { deliveryMedium: 'EMAIL', destination: email }
        };
      } else {
        throw new Error('All fields are required');
      }
    } catch (err) {
      const errorMessage = getAuthErrorMessage(err);
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const confirmSignUp = async (email, confirmationCode) => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Simulate confirmation - in production this would use AWS Amplify
      if (confirmationCode === '123456') {
        return true;
      } else {
        throw new Error('Invalid verification code');
      }
    } catch (err) {
      const errorMessage = getAuthErrorMessage(err);
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const signOut = async () => {
    try {
      setIsLoading(true);
      console.log('🚪 Starting logout process...');
      
      if (config.isDemoMode) {
        console.log('🎭 DEMO MODE: Clearing localStorage');
        localStorage.removeItem('demo-user');
      } else {
        console.log('🚀 PRODUCTION MODE: Would sign out from Cognito');
        // await Auth.signOut(); // Would be used in production
        localStorage.clear(); // Clear all data for production mode
      }
      
      setUser(null);
      setError(null);
      console.log('✅ Logout completed successfully');
    } catch (err) {
      console.error('❌ Error signing out:', err);
      setError('Failed to sign out');
    } finally {
      setIsLoading(false);
    }
  };

  const updateProfile = async (profileData) => {
    try {
      setIsLoading(true);
      setError(null);

      const updatedUser = {
        ...user,
        firstName: profileData.firstName !== undefined ? profileData.firstName : user.firstName,
        lastName: profileData.lastName !== undefined ? profileData.lastName : user.lastName
      };
      
      localStorage.setItem('demo-user', JSON.stringify(updatedUser));
      setUser(updatedUser);
      
      return true;
    } catch (err) {
      const errorMessage = getAuthErrorMessage(err);
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const changePassword = async (oldPassword, newPassword) => {
    try {
      setIsLoading(true);
      setError(null);

      // Simulate password change - in production this would use AWS Amplify
      if (oldPassword && newPassword && newPassword.length >= 8) {
        return true;
      } else {
        throw new Error('Invalid password requirements');
      }
    } catch (err) {
      const errorMessage = getAuthErrorMessage(err);
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const forgotPassword = async (email) => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Simulate forgot password - in production this would use AWS Amplify
      return { codeDeliveryDetails: { deliveryMedium: 'EMAIL', destination: email } };
    } catch (err) {
      const errorMessage = getAuthErrorMessage(err);
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const forgotPasswordSubmit = async (email, confirmationCode, newPassword) => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Simulate password reset - in production this would use AWS Amplify
      if (confirmationCode === '123456' && newPassword.length >= 8) {
        return true;
      } else {
        throw new Error('Invalid code or password requirements');
      }
    } catch (err) {
      const errorMessage = getAuthErrorMessage(err);
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const resendSignUp = async (email) => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Simulate resend - in production this would use AWS Amplify
      return true;
    } catch (err) {
      const errorMessage = getAuthErrorMessage(err);
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function to check permissions
  const hasPermission = (permission) => {
    if (!user) return false;
    return user.permissions.includes(permission);
  };

  // Helper function to check if user is admin
  const isAdmin = () => {
    return user?.role === UserRole.ADMIN;
  };

  // Helper function to get user-friendly error messages
  const getAuthErrorMessage = (error) => {
    if (error.message) return error.message;
    
    switch (error.code) {
      case 'UserNotFoundException':
        return 'No account found with this email address.';
      case 'NotAuthorizedException':
        return 'Incorrect email or password.';
      case 'UserNotConfirmedException':
        return 'Please confirm your email address before signing in.';
      case 'PasswordResetRequiredException':
        return 'Password reset is required. Please reset your password.';
      case 'UserAlreadyExistsException':
        return 'An account with this email already exists.';
      case 'InvalidPasswordException':
        return 'Password does not meet requirements.';
      case 'CodeMismatchException':
        return 'Invalid verification code.';
      case 'ExpiredCodeException':
        return 'Verification code has expired.';
      case 'LimitExceededException':
        return 'Too many attempts. Please try again later.';
      case 'TooManyFailedAttemptsException':
        return 'Too many failed attempts. Account temporarily locked.';
      case 'InvalidParameterException':
        return 'Invalid input provided.';
      case 'UsernameExistsException':
        return 'An account with this email already exists.';
      default:
        return error.message || 'An unexpected error occurred.';
    }
  };

  // Clear error function
  const clearError = () => {
    setError(null);
  };

  const contextValue = {
    user,
    isLoading,
    error,
    signIn,
    signUp,
    confirmSignUp,
    signOut,
    updateProfile,
    changePassword,
    forgotPassword,
    forgotPasswordSubmit,
    resendSignUp,
    hasPermission,
    isAdmin,
    clearError,
    checkUser
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;